import React, { useCallback, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Dashboard,
  DashboardOwner,
  DashboardUserPermission,
  SortingType,
} from 'utils/customTypes';
import classnames from 'classnames';
import sparkles from 'assets/icons/sparkles-current-color.svg';
import isEmpty from 'lodash/isEmpty';
import {
  DASHBOARDS_LIST_ACTIONS,
  DASHBOARDS_TABLE_SORTABLE_COLUMNS,
} from 'utils/constants/dashboard';
import { PATHS, USER_ROLES, V2_PATHS } from 'utils/constants';
import intl from 'react-intl-universal';
import { Link, useHistory } from 'react-router-dom';
import {
  AvatarGroup,
  Button,
  Icon,
  OverflowMenu,
  OverflowMenuItem,
  Table,
  Tag,
  tailwindOverride,
  Typography,
} from '@getsynapse/design-system';
import noDashboardsFound from 'assets/images/no-projects-found-table.svg';
import emptyDashboardsTable from 'assets/images/empty-dashboard.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  dashboardSelectors,
  getDashboardById,
  setSort,
} from 'state/normalized/dashboard/dashboardSlice';
import { agentDashboardSelectors } from 'state/normalized/agentDashboard/agentDashboardSlice';
import RenderNoRecords from 'Atoms/NoRecords/NoRecords';
import { selectUserRole } from 'state/User/userSlice';
import { DashboardContext } from 'Pages/DashboardsDetailsPage/DashboardContext';
import { DeleteDashboardOverflowMenuItem } from '../../DashboardsDetailsPage/components/DashboardContentViewOverflowMenu/DeleteDashboardOverflowMenuItem';
import { DashboardSharedOverflowMenu } from 'Pages/DashboardsDetailsPage/components/DashboardSharedOverflowMenu';
import { AgentDashboardOverflowMenu } from './AgentDashboardOverflowMenu';

type OwnerAvatarProps = {
  key: string;
  imageSrc: string;
  initial: string;
  name: string;
  disabled: boolean;
};

type DashboardRow = Dashboard & { isAgentGenerated?: boolean };

const DashboardsTable: React.FC<{
  createNewDashboard?: () => void;
  onEditDashboardName?: ((dashboardId: string) => void) | undefined;
  searchValue?: string;
}> = ({ createNewDashboard, onEditDashboardName, searchValue }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const dashboardItems = useSelector(dashboardSelectors.selectAll);
  const agentDashboardItems = useSelector(agentDashboardSelectors.selectAll);
  const dashboardAffordances = useSelector(
    dashboardSelectors.selectAllDashboardAffordances
  );
  const userRole = useSelector(selectUserRole);
  const [newOwnerModalOpen, setNewOwnerModalOpen] = useState(false);

  const { canCreate } = useSelector(
    dashboardSelectors.selectGlobalAffordanceFlags
  );

  const {
    dashboardListPageOwnerManagement = false,
    flexibleDashboardShare = false,
    reportingAgentAccess = false,
  } = useFlags();

  const dashboardList = useMemo(() => {
    const matchesSearch = (item: Dashboard) =>
      !searchValue?.length ||
      item.name.toLowerCase().includes(searchValue.toLowerCase());

    const agentItems: DashboardRow[] = reportingAgentAccess
      ? agentDashboardItems
          .filter(matchesSearch)
          .map((item) => ({ ...item, isAgentGenerated: true }))
      : [];

    const items: DashboardRow[] = [
      ...agentItems,
      ...dashboardItems.filter(matchesSearch),
    ];

    return {
      data: items,
      total: items.length,
    };
  }, [dashboardItems, agentDashboardItems, reportingAgentAccess, searchValue]);

  const isDashboardsListEmpty = useMemo(
    () => dashboardList.total === 0,
    [dashboardList]
  );

  const mapOwnerToAvatar = useCallback(
    (owner: DashboardOwner): OwnerAvatarProps => ({
      key: owner.id,
      imageSrc: '',
      initial: `${owner.firstName?.charAt(0) ?? ''}${
        owner.lastName?.charAt(0) ?? ''
      }`,
      name: `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim(),
      disabled: owner.disabled ?? false,
    }),
    []
  );

  const ownerAvatarsById = useMemo(() => {
    const map = new Map<string, OwnerAvatarProps>();
    dashboardList.data.forEach((dashboard) => {
      (dashboard.owners ?? []).forEach((owner) => {
        if (owner.id && !map.has(owner.id)) {
          map.set(owner.id, mapOwnerToAvatar(owner));
        }
      });
    });
    return map;
  }, [dashboardList.data, mapOwnerToAvatar]);

  const getDashboardPath = (dashboard: DashboardRow) =>
    dashboard.isAgentGenerated
      ? `${V2_PATHS.DASHBOARD_PAGE}/${dashboard.id}`
      : `${PATHS.DASHBOARD_PAGE}/${dashboard.id}`;

  const goToDashboard = (dashboard: DashboardRow) => {
    if (dashboard.isAgentGenerated) {
      // V2 dashboards live in a separate bundle — use a full-page navigation.
      window.location.assign(getDashboardPath(dashboard));
    } else {
      history.push(getDashboardPath(dashboard));
    }
  };

  const handleSort = (orderByParam: string, order: SortingType) => {
    dispatch(setSort({ orderBy: orderByParam, sortingType: order }));
  };

  const handleNewOwnerModal = useCallback(
    (dashboardId: string) => {
      setNewOwnerModalOpen(true);
      dispatch(getDashboardById({ dashboardId }));
    },
    [dispatch]
  );

  const handleManageDashboard = useCallback(
    (dashboardId: string) => {
      setManageDashboardModalOpen(true);
      dispatch(getDashboardById({ dashboardId }));
    },
    [dispatch]
  );

  const [manageDashboardModalOpen, setManageDashboardModalOpen] =
    useState(false);

  //no-op empty placeholder component:
  const NewOwnerDashboardModal: React.FC<{
    isOpen: boolean;
    newOwnerDashboardModal: (dashboardId: string) => void;
    onCloseModal: () => void;
    afterDuplicateDashboard?: (newId: string) => void;
  }> = () => {
    return <> </>;
  };

  const ManageDashboardModal: React.FC<{
    isOpen: boolean;
    manageAccessDashboard: (dashboardId: string) => void;
    onCloseModal: () => void;
    afterDuplicateDashboard?: (newId: string) => void;
  }> = () => {
    return <> </>;
  };

  return (
    <div
      className={classnames('w-full rounded-b', {
        'overflow-auto border border-neutral-lighter-two':
          !isDashboardsListEmpty,
        'overflow-hidden border-r border-neutral-lighter-two min-h-88':
          isDashboardsListEmpty,
        'max-h-no-filtered-table-body': true,
      })}
    >
      <Table
        className={classnames('relative', {
          'max-w-full border-0 w-full': !isDashboardsListEmpty,
          'w-full min-h-88': isDashboardsListEmpty,
        })}
        canSelectRows={false}
        data={{
          headData: {
            stickyHeader: true,
            handleSort,
            headCells: [
              {
                'data-cy': 't-header__name',
                columnName: DASHBOARDS_TABLE_SORTABLE_COLUMNS.NAME,
                content: intl.get('DASHBOARDS_LIST_PAGE.TABLE.HEAD.NAME'),
              },
              {
                'data-cy': 't-header__owner-name',
                columnName: DASHBOARDS_TABLE_SORTABLE_COLUMNS.OWNER,
                content: intl.get('DASHBOARDS_LIST_PAGE.TABLE.HEAD.OWNER'),
              },
              {
                'data-cy': 't-header__created-at',
                columnName: DASHBOARDS_TABLE_SORTABLE_COLUMNS.CREATED_ON,
                content: intl.get('DASHBOARDS_LIST_PAGE.TABLE.HEAD.CREATED_ON'),
              },
              {
                'data-cy': 't-header__last-viewed',
                columnName: DASHBOARDS_TABLE_SORTABLE_COLUMNS.LAST_UPDATED,
                content: intl.get(
                  'DASHBOARDS_LIST_PAGE.TABLE.HEAD.LAST_UPDATED'
                ),
              },
              {
                'data-cy': 't-header__health',
                columnName: DASHBOARDS_TABLE_SORTABLE_COLUMNS.PERMISSIONS,
                content: intl.get(
                  'DASHBOARDS_LIST_PAGE.TABLE.HEAD.PERMISSIONS'
                ),
              },
              {
                content: <div></div>,
                className: classnames('h-10 bg-primary-lightest z-2', {
                  'right-0': !isDashboardsListEmpty,
                }),
              },
            ],
          },
          rows: dashboardList.data.map((dashboard: DashboardRow, _) => {
            const isAgentGenerated = dashboard.isAgentGenerated ?? false;
            const owners = dashboard.owners ?? [];
            const primaryOwner = owners?.[0] ?? null;
            const permission =
              dashboard.permission ?? DashboardUserPermission.VIEWER;
            const affordances = dashboardAffordances?.[dashboard.id] ?? {};
            // Agent-generated dashboards can't be shared, deleted or edited yet.
            const canEdit =
              !isAgentGenerated && affordances[DASHBOARDS_LIST_ACTIONS.EDIT];
            const canDelete =
              !isAgentGenerated && affordances[DASHBOARDS_LIST_ACTIONS.DELETE];
            const canShare =
              !isAgentGenerated && affordances[DASHBOARDS_LIST_ACTIONS.SHARE];
            // Agent-generated dashboards: only the owner (creator) can list shares.
            const canViewAgentShares =
              isAgentGenerated && permission === DashboardUserPermission.OWNER;

            const overflowMenuItems: {
              label: string;
              iconName?: string;
              iconSrc?: string;
              onSelect: () => void;
              action: typeof DASHBOARDS_LIST_ACTIONS[keyof typeof DASHBOARDS_LIST_ACTIONS];
            }[] = [
              {
                label: intl.get('DASHBOARDS_LIST_PAGE.TABLE.ACTION.EDIT'),
                iconName: 'pencil-outline',
                onSelect: () => {
                  onEditDashboardName && onEditDashboardName(dashboard.id);
                },
                action: DASHBOARDS_LIST_ACTIONS.EDIT,
              },
            ];

            return {
              id: dashboard.id,
              'data-cy': `dashboard-${dashboard.id}`,
              className: classnames('group cursor-pointer relative'),
              onClick: () => goToDashboard(dashboard),
              cells: [
                {
                  content: (
                    <div
                      className={classnames('flex items-center gap-x-2', {
                        'h-4': !isDashboardsListEmpty,
                        'w-full': isDashboardsListEmpty,
                      })}
                    >
                      {isAgentGenerated ? (
                        <>
                          {/* Marks the row as the reporting agent's work, so a
                              mixed list is readable at a glance. Kept to one
                              line so rows stay a uniform height. */}
                          <Icon
                            src={sparkles}
                            className='w-4 h-4 flex-shrink-0 text-secondary-darker'
                            data-cy={`dashboard-${dashboard.id}-ai-icon`}
                          />
                          <a
                            href={getDashboardPath(dashboard)}
                            title={dashboard.name}
                            className='truncate'
                          >
                            {dashboard.name}
                          </a>
                          <Tag
                            label={intl.get(
                              'DASHBOARDS_LIST_PAGE.TABLE.AI_TAG'
                            )}
                            className='flex-shrink-0 py-0 px-1.5 bg-secondary-lightest'
                            textClassName='text-xs leading-4 text-secondary-darker'
                            data-cy={`dashboard-${dashboard.id}-ai-tag`}
                          />
                        </>
                      ) : (
                        <Link
                          to={{
                            pathname: getDashboardPath(dashboard),
                            state: {
                              from: history.location.pathname,
                            },
                          }}
                          title={dashboard.name}
                        >
                          {dashboard.name}
                        </Link>
                      )}
                    </div>
                  ),
                  'data-cy': `dashboard-${dashboard.id}-title`,
                },
                {
                  content: (
                    <div className='flex items-center'>
                      {owners.length > 0 ? (
                        <>
                          <AvatarGroup
                            avatars={owners.map(
                              (owner) =>
                                ownerAvatarsById.get(owner.id) ??
                                mapOwnerToAvatar(owner)
                            )}
                            deactivatedText={intl.get('DEACTIVATED')}
                          />
                          {owners.length === 1 && (
                            <div className='flex flex-row justify-between items-center gap-2.5 ml-2.5'>
                              <Typography
                                variant='body2'
                                className={tailwindOverride('', {
                                  'text-neutral-dark': primaryOwner?.disabled,
                                })}
                              >
                                {`${primaryOwner?.firstName ?? ''} ${
                                  primaryOwner?.lastName ?? ''
                                }`.trim()}
                              </Typography>
                              {primaryOwner?.disabled && (
                                <Tag
                                  label={intl.get('DEACTIVATED')}
                                  className='bg-neutral-lighter-two'
                                  textClassName='text-neutral-dark'
                                />
                              )}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  ),
                },
                {
                  content: dashboard.createdAt,
                },
                {
                  content: dashboard.updatedAt,
                },
                {
                  content: (
                    <div className=''>
                      {intl.get(
                        `DASHBOARDS_LIST_PAGE.TABLE.HEAD.${permission.toUpperCase()}`
                      )}
                    </div>
                  ),
                },
                {
                  content: (
                    <div
                      className={classnames(
                        'h-full text-center relative flex space-x-2',
                        {
                          'w-full': isDashboardsListEmpty,
                        }
                      )}
                      onClick={(event: React.MouseEvent<HTMLInputElement>) =>
                        event.stopPropagation()
                      }
                    >
                      {
                        <>
                          {flexibleDashboardShare && canShare ? (
                            <DashboardSharedOverflowMenu
                              isIconButton
                              dashboardId={dashboard.id}
                            />
                          ) : null}

                          {canViewAgentShares ? (
                            <DashboardSharedOverflowMenu
                              isIconButton
                              isAgentGenerated
                              dashboardId={dashboard.id}
                            />
                          ) : null}

                          {canViewAgentShares ? (
                            <AgentDashboardOverflowMenu
                              dashboardId={dashboard.id}
                              dashboardName={dashboard.name}
                              conversationId={dashboard.conversationId}
                            />
                          ) : null}

                          {canEdit || canDelete ? (
                            <OverflowMenu
                              menuListProps={{
                                'data-cy': `dashboard-${dashboard.id}__actions-popover`,
                              }}
                              menuButtonProps={{
                                'data-cy': `dashboard-${dashboard.id}__actions-popover-trigger`,
                              }}
                            >
                              {overflowMenuItems.map((item) =>
                                affordances[item.action] ? (
                                  <OverflowMenuItem
                                    key={item.label}
                                    className='group flex items-center space-x-2'
                                    onSelect={item.onSelect}
                                    data-cy={`dashboard-${dashboard.id}__actions-popover__${item.action}`}
                                  >
                                    <Icon
                                      name={item.iconName || ''}
                                      src={item.iconSrc || ''}
                                      className={tailwindOverride(
                                        'fill-current text-primary',
                                        'group-hover:fill-current group-hover:text-secondary-darker',
                                        'group-active:text-neutral-white group-active:fill-neutral-white',
                                        'text-lg',
                                        {
                                          'text-xl': item.iconSrc,
                                        }
                                      )}
                                    />
                                    <Typography
                                      variant='label'
                                      className={tailwindOverride(
                                        'group-active:text-neutral-white',
                                        'cursor-pointer'
                                      )}
                                    >
                                      {item.label}
                                    </Typography>
                                  </OverflowMenuItem>
                                ) : null
                              )}
                              <DashboardContext.Provider
                                value={{ dashboardId: dashboard.id }}
                              >
                                {canDelete ? (
                                  <DeleteDashboardOverflowMenuItem />
                                ) : null}
                              </DashboardContext.Provider>
                            </OverflowMenu>
                          ) : null}
                        </>
                      }
                    </div>
                  ),
                },
              ],
            };
          }),
          total: dashboardList.total,
        }}
        data-cy='team-dashboards-table'
        emptyComponent={
          !isEmpty(searchValue) ? (
            <RenderNoRecords
              imageSrc={noDashboardsFound}
              className='h-empty-table-body'
              caption={intl.get('DASHBOARDS_LIST_PAGE.NO_RECORDS')}
            />
          ) : (
            <RenderNoRecords
              imageSrc={emptyDashboardsTable}
              className='h-empty-table-body'
              caption={
                userRole === USER_ROLES.ADMIN
                  ? intl.get('DASHBOARDS_LIST_PAGE.TABLE.EMPTY')
                  : intl.get('DASHBOARDS_LIST_PAGE.TABLE.EMPTY_SHARE_DASHBOARD')
              }
              labelClassName={tailwindOverride('mt-0 text-xl', {
                'whitespace-normal text-center max-w-72':
                  userRole !== USER_ROLES.ADMIN,
              })}
            >
              <div className='flex flex-col mt-2 items-center justify-center gap-2'>
                {userRole === USER_ROLES.ADMIN ? (
                  <>
                    <Typography variant='body2' className='mt-2'>
                      {intl.get('DASHBOARDS_LIST_PAGE.TABLE.EMPTY_SUB_CAPTION')}
                    </Typography>
                    {canCreate && (
                      <Button className='mx-auto' onClick={createNewDashboard}>
                        {intl.get(
                          'DASHBOARDS_LIST_PAGE.TABLE.CREATE_DASHBOARD'
                        )}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    className='mx-auto'
                    variant='secondary'
                    onClick={() => {}}
                  >
                    {intl.get('DASHBOARDS_LIST_PAGE.TABLE.LEARN_MORE')}
                  </Button>
                )}
              </div>
            </RenderNoRecords>
          )
        }
      />
      {dashboardListPageOwnerManagement && (
        <>
          <NewOwnerDashboardModal
            isOpen={newOwnerModalOpen}
            newOwnerDashboardModal={handleNewOwnerModal}
            onCloseModal={() => setNewOwnerModalOpen(false)}
          />
          <ManageDashboardModal
            isOpen={manageDashboardModalOpen}
            manageAccessDashboard={handleManageDashboard}
            onCloseModal={() => setManageDashboardModalOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default DashboardsTable;
